import { useRef, useState, type CSSProperties, type DragEvent } from 'react';

import { cx } from './classNames';

/** Pendant zu Syncfusions `FileInfo`. */
export interface UploaderFile {
  name: string;
  size: number;
  /** Endung ohne Punkt, kleingeschrieben — wie `FileInfo.Type`. */
  type: string;
  mimeContentType: string;
  file: File;
}

/** Pendant zu `SfUploader`. */
export interface UploaderProps {
  /** Altclient: `false`. */
  allowMultiple?: boolean;
  /** Kommaliste wie `".pdf,.docx"`. */
  allowedExtensions?: string;
  /** Obergrenze in Bytes; `0` oder `undefined` heißt „ohne Grenze". */
  maxFileSize?: number;
  onChange: (files: UploaderFile[]) => void;
  onRemove: () => void;
  id?: string;
  className?: string;
  style?: CSSProperties;
  title?: string;
}

interface Entry {
  info: UploaderFile;
  error?: string;
}

const INVALID_TYPE = 'File type is not allowed';
const INVALID_SIZE = 'File size is too large';

function toInfo(file: File): UploaderFile {
  const dot = file.name.lastIndexOf('.');
  return {
    name: file.name,
    size: file.size,
    type: dot === -1 ? '' : file.name.slice(dot + 1).toLowerCase(),
    mimeContentType: file.type,
    file,
  };
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${String(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function Uploader(props: UploaderProps) {
  const { allowedExtensions, maxFileSize, onChange } = props;
  const [entries, setEntries] = useState<Entry[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const validate = (info: UploaderFile): string | undefined => {
    const allowed = (allowedExtensions ?? '')
      .split(',')
      .map((extension) => extension.trim().replace(/^\./, '').toLowerCase())
      .filter((extension) => extension !== '');
    if (allowed.length > 0 && !allowed.includes(info.type)) return INVALID_TYPE;
    if (maxFileSize !== undefined && maxFileSize > 0 && info.size > maxFileSize)
      return INVALID_SIZE;
    return undefined;
  };

  const accept = (fileList: FileList | null) => {
    if (fileList === null || fileList.length === 0) return;

    const picked = Array.from(fileList).map((file) => {
      const info = toInfo(file);
      return { info, error: validate(info) };
    });
    const next = props.allowMultiple === true ? [...entries, ...picked] : picked.slice(0, 1);
    setEntries(next);
    onChange(next.filter((entry) => entry.error === undefined).map((entry) => entry.info));
  };

  const remove = (index: number) => {
    setEntries((current) => current.filter((_entry, position) => position !== index));
    props.onRemove();
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    accept(event.dataTransfer.files);
  };

  return (
    <div
      id={props.id}
      title={props.title}
      style={props.style}
      className={cx('e-upload', props.className)}
      onDragOver={(event) => {
        event.preventDefault();
      }}
      onDrop={onDrop}
    >
      <div className="e-file-select-wrap">
        <span className="e-file-select">
          <button
            type="button"
            className="e-btn e-css"
            onClick={() => {
              inputRef.current?.click();
            }}
          >
            Browse...
          </button>
          {/* Das Feld ist auf 1 x 1 px und `opacity: 0` gesetzt (_uploader.scss)
              und war als Tabstopp ein unsichtbarer Fokushalt — ausgelöst wird es
              über die sichtbare Schaltfläche daneben. Es bleibt im
              Bedienungsbaum, damit Hilfsmittel es weiter finden; nur die
              Tabulatorreihenfolge lässt es aus (REQ-17 §3). */}
          <input
            ref={inputRef}
            type="file"
            tabIndex={-1}
            multiple={props.allowMultiple === true}
            accept={allowedExtensions}
            onChange={(event) => {
              accept(event.target.files);
              event.target.value = '';
            }}
          />
        </span>
        <span className="e-file-drop">Or drop files here</span>
      </div>
      {entries.length === 0 ? null : (
        <ul className="e-upload-files">
          {entries.map((entry, index) => (
            <li key={`${entry.info.name}-${String(index)}`} className="e-upload-file-list">
              <span className="e-file-container">
                <span className="e-file-name">{entry.info.name}</span>
                <span className="e-file-type">.{entry.info.type}</span>
                <span className="e-file-size">{formatSize(entry.info.size)}</span>
                {entry.error === undefined ? null : (
                  <span className="e-file-status e-validation-fails">{entry.error}</span>
                )}
              </span>
              <button
                type="button"
                title="Remove"
                aria-label="Remove"
                className="e-icons e-file-remove-btn"
                onClick={() => {
                  remove(index);
                }}
              >
                <i className="fa-solid fa-xmark" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default Uploader;
